import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Lobby from "./pages/Lobby";
import Sportsbook from "./pages/Sportsbook";
import Tournaments from "./pages/Tournaments";
import Transactions from "./pages/Transactions";
import Profile from "./pages/Profile";
import AdminDashboard from "./pages/AdminDashboard";
import FlamingoAudioAudition from "./pages/admin/FlamingoAudioAudition";
import Leaderboard from "./pages/Leaderboard";
import VIPPage from "./pages/VIPPage";
import Promotions from "./pages/Promotions";
import Referrals from "./pages/Referrals";
import DistributorDashboard from "./pages/DistributorDashboard";
import OperatorDashboard from "./pages/OperatorDashboard";
import AffiliateDashboard from "./pages/AffiliateDashboard";
import Lottery from "./pages/Lottery";
import RegularSlots from "./pages/RegularSlots";
import BlackjackV2 from "./pages/BlackjackV2";
import PokerComingSoon from "./pages/PokerComingSoon";
// Poker Room hidden while we polish it — routes now render PokerComingSoon.
// Keeping the imports commented (not deleted) so we can re-enable easily.
// import PokerLobby from "./pages/PokerLobby";
// import PokerTable from "./pages/PokerTable";
import ThreeCardPoker from "./pages/ThreeCardPoker";
import Craps from "./pages/Craps";
import Roulette from "./pages/Roulette";
import StreetDiceLobby from "./pages/StreetDiceLobby";
import KenoRoom from "./pages/games/KenoRoom";
import SuperDoubleUp from "./pages/games/SuperDoubleUp";
import LuckyDucky from "./games/luckyDucky/LuckyDucky";
import LuckyDuckySim from "./games/luckyDucky/LuckyDuckySim";
import LuckyDuckyRoute from "./games/luckyDuckyIncisor/LuckyDuckyRoute";
import Keno4HeartsRoute from "./games/kenoHeartsV2/Keno4HeartsRoute";
import BtpbRoute from "./games/btpbDemo/BtpbRoute";
import LfRoute from "./games/luckyFlamingoV2/LfRoute";
import BbRoute from "./games/buffaloBucks/BbRoute";
import DeepSeaFurtuneStage from "./games/deepSeaFurtune/DeepSeaFurtuneStage";
import { DailySuperMeter } from "./components/DailySuperMeter";
import { DailySuperHit } from "./components/DailySuperHit";
import SpinBreakdownToast from "./components/SpinBreakdownToast";
import BofRoute from "./games/bofV2/BofRoute";
import ThunderCash from "./games/thunderCash/ThunderCash";
import PandaParadise from "./games/pandaParadise/PandaParadise";
import OutlawsLoot from "./games/outlawsLoot/OutlawsLoot";
const THUNDER_CASH_ENABLED = process.env.REACT_APP_THUNDER_CASH_ENABLED !== "false";
const PANDA_PARADISE_ENABLED = process.env.REACT_APP_PANDA_PARADISE_ENABLED !== "false";
// Street Dice is hidden in production (pending polish). Set
// REACT_APP_STREET_DICE_ENABLED=true in frontend/.env to work on it in preview.
const STREET_DICE_ENABLED = process.env.REACT_APP_STREET_DICE_ENABLED === "true";

import StreetDiceRoom from "./pages/StreetDiceRoom";
import StreetDiceShell from "./pages/StreetDiceShell";
import LowBalanceNudge from "./components/LowBalanceNudge"; // eslint-disable-line no-unused-vars

import TonkLobby from "./pages/TonkLobby";
import TonkRoom from "./pages/TonkRoom";
import Rewards from "./pages/Rewards";
import SendCredits from "./pages/SendCredits";
import AmbassadorApply from "./pages/AmbassadorApply";
import KioskPage from "./pages/KioskPage";
import BuyCredits from "./pages/BuyCredits";
import CashierDashboard from "./pages/CashierDashboard";
import Help from "./pages/Help";
import InstallBonusModal from "./components/InstallBonusModal";
// import PayoutSpeedBanner from "./components/PayoutSpeedBanner"; // disabled Jun 2026 (3-hour payout popup off)
import SupportChatFab from "./components/SupportChatFab";
import TonkAutoDropWatcher from "./components/TonkAutoDropWatcher";
import JackpotFireCelebration from "./components/JackpotFireCelebration";  // eslint-disable-line no-unused-vars -- community pots removed from UI (Jun 2026)
import BigWinCelebration from "./components/BigWinCelebration";
import MatchUnlockedCelebration from "./components/MatchUnlockedCelebration";
import WhatsNewModal from "./components/WhatsNewModal";
import { Toaster } from "./components/ui/sonner";
import ForceRealEmailGate from "./components/ForceRealEmailGate";
import "./toastCalmdown"; // side-effect: patches sonner.toast.error/.warning globally
import { toast } from "sonner";
import { useEffect } from "react";
import { useBigText } from "./hooks/useBigText";

// Register service worker for PWA + push notifications
function useServiceWorker() {
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        let reloading = false;
        let toastShown = false;
        // On a user's FIRST visit there is no controller yet; when the freshly
        // installed SW claims the page, 'controllerchange' fires. Reloading then
        // wipes in-flight app state. Only reload on a genuine UPDATE (a controller
        // already existed at mount, i.e. SKIP_WAITING was applied).
        const hadControllerAtMount = !!navigator.serviceWorker.controller;

        const promptUpdate = (worker) => {
            if (toastShown) return;
            toastShown = true;
            toast.success('App updated — tap to reload', {
                description: 'New features and fixes are ready.',
                duration: Infinity,
                action: {
                    label: 'Reload',
                    onClick: () => {
                        worker.postMessage({ type: 'SKIP_WAITING' });
                    },
                },
            });
        };

        navigator.serviceWorker.register('/sw.js')
            .then((reg) => {
                console.log('SW registered:', reg.scope);
                // If there's already a waiting SW when the page loads, prompt now.
                if (reg.waiting && navigator.serviceWorker.controller) {
                    promptUpdate(reg.waiting);
                }
                // Watch for a new SW being installed.
                reg.addEventListener('updatefound', () => {
                    const newWorker = reg.installing;
                    if (!newWorker) return;
                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            // A fresh SW is installed AND there's an old one in control — update available.
                            promptUpdate(newWorker);
                        }
                    });
                });
                // Force-check for updates on every app load — pushes new SWs to existing users.
                reg.update().catch(() => {});
            })
            .catch((err) => console.warn('SW registration failed:', err));

        // When the new SW takes control (after SKIP_WAITING), reload once.
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (reloading) return;
            if (!hadControllerAtMount) return; // skip spurious first-install reload
            reloading = true;
            window.location.reload();
        });
    }, []);
}

// Wrapper: redirects non-player roles to their own portal when they hit a player page.
// NOTE: ambassadors and admins are intentionally NOT redirected — a person can wear
// two hats (e.g. an ambassador who also plays). They reach the games directly and hop
// back to their portal via the header link ("Ambassador Portal" / "Admin").
const PlayerRoute = ({ children }) => {
    const { user } = useAuth();
    if (user?.role === 'distributor') return <Navigate to="/distributor" replace />;
    if (user?.role === 'operator') return <Navigate to="/operator" replace />;
    if (user?.role === 'cashier') return <Navigate to="/cashier" replace />;
    return children;
};

// Only mount the support chat FAB for authenticated players (role === "user").
// Suppress inside the Keno 4 Hearts room so the FAB doesn't cover the
// PLAY button on landscape phones.
const PlayerOnlyChatFab = () => {
    const { user } = useAuth();
    const { pathname } = useLocation();
    if (!user || user.role !== 'user') return null;
    if (/^\/casino\/keno-4-hearts(\/|$)/.test(pathname)) return null;
    return <SupportChatFab />;
};

// Casino Jukebox / Now-Playing ticker were removed on 2026-02-15 per player
// feedback ("complete remove the music option"). Keeping the imports absent
// entirely so the audio element never mounts and the pill never renders.

function App() {
    useServiceWorker();
    useBigText();

    return (
        <AuthProvider>
            <div className="App">
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/ambassadors" element={<AmbassadorApply />} />
                        <Route path="/kiosk" element={<KioskPage />} />
                        <Route
                            path="/lobby"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Lobby /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/buy-credits"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><BuyCredits /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/sportsbook"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Sportsbook /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/lottery"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Lottery /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        {/* Majik Bonus is off (owner decision): its page sends players to regular slots */}
                        <Route path="/casino" element={<Navigate to="/casino/slots" replace />} />
                        <Route path="/slots" element={<Navigate to="/casino/slots" replace />} />
                        <Route
                            path="/casino/slots"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><RegularSlots /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/casino/blackjack"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><BlackjackV2 /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/casino/blackjack-v2"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><BlackjackV2 /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/poker"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><ThreeCardPoker /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/casino/three-card-poker"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><ThreeCardPoker /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/casino/craps"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Craps /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/craps"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Craps /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/casino/roulette"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Roulette /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/roulette"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Roulette /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/casino/poker"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><PokerComingSoon /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        {/* Street Dice & Tonk — hidden 2026-08-02 pending polish.
                            Street Dice is env-gated: enabled in preview via
                            REACT_APP_STREET_DICE_ENABLED=true, still hidden in
                            production (routes redirect to /lobby there). Tonk
                            remains fully hidden until it's ready. */}
                        <Route path="/casino/street-dice"     element={STREET_DICE_ENABLED ? <StreetDiceLobby /> : <Navigate to="/lobby" replace />} />
                        <Route path="/casino/street-dice/preview-shell" element={STREET_DICE_ENABLED ? <StreetDiceShell /> : <Navigate to="/lobby" replace />} />
                        <Route path="/casino/keno"            element={<KenoRoom />} />
                        <Route path="/casino/keno-4-hearts"   element={<Keno4HeartsRoute />} />
                        <Route path="/casino/keno-4-hearts-classic" element={<Navigate to="/casino/keno-4-hearts" replace />} />
                        <Route path="/casino/keno-super-double" element={<SuperDoubleUp />} />
                        <Route path="/casino/lucky-ducky" element={<LuckyDuckyRoute />} />
                        <Route path="/casino/lucky-ducky-classic" element={<LuckyDucky />} />
                        <Route path="/casino/lucky-ducky/sim" element={<LuckyDuckySim />} />
                        <Route path="/casino/lucky-flamingo" element={<LfRoute />} />
                        <Route path="/casino/deep-sea-furtune" element={<DeepSeaFurtuneStage />} />
                        <Route path="/casino/deep-sea-furtune-stage" element={<DeepSeaFurtuneStage />} />
                        <Route path="/casino/buffalo-bucks" element={<ProtectedRoute><PlayerRoute><BbRoute /></PlayerRoute></ProtectedRoute>} />
                        <Route path="/casino/balls-of-fire" element={<BofRoute />} />
                        <Route path="/casino/balls-of-fire/kiosk" element={<BofRoute />} />
                        <Route path="/casino/thunder-cash" element={THUNDER_CASH_ENABLED ? <ThunderCash /> : <Navigate to="/casino" replace />} />
                        <Route path="/casino/thunder-cash/kiosk" element={THUNDER_CASH_ENABLED ? <ThunderCash /> : <Navigate to="/casino" replace />} />
                        <Route path="/casino/panda-paradise" element={PANDA_PARADISE_ENABLED ? <PandaParadise /> : <Navigate to="/casino" replace />} />
                        <Route path="/casino/outlaws-loot"    element={<OutlawsLoot />} />
                        <Route path="/casino/btpb"            element={<BtpbRoute />} />
                        <Route path="/casino/street-dice/:id" element={STREET_DICE_ENABLED ? <StreetDiceRoom /> : <Navigate to="/lobby" replace />} />
                        <Route path="/casino/tonk"            element={<Navigate to="/lobby" replace />} />
                        <Route path="/casino/tonk/:id"        element={<Navigate to="/lobby" replace />
                            }
                        />
                        <Route
                            path="/casino/poker/:tid"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><PokerComingSoon /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/poker-coming-soon"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><PokerComingSoon /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/rewards"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Rewards /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/tournaments"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Tournaments /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/transactions"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Transactions /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/send"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><SendCredits /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Profile /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute adminOnly>
                                    <AdminDashboard />
                                </ProtectedRoute>
                            }
                        />
                        {/* Hidden admin-only audio audition panel for Lucky Flamingo.
                            Not linked from any nav — reachable only via direct URL. */}
                        <Route
                            path="/admin/flamingo-audio"
                            element={
                                <ProtectedRoute adminOnly>
                                    <FlamingoAudioAudition />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/leaderboard"
                            element={<Navigate to="/sportsbook" replace />}
                        />
                        <Route
                            path="/vip"
                            element={<Navigate to="/sportsbook" replace />}
                        />
                        <Route
                            path="/promotions"
                            element={<Navigate to="/sportsbook" replace />}
                        />
                        <Route
                            path="/referrals"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><Referrals /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/distributor"
                            element={
                                <ProtectedRoute>
                                    <PlayerRoute><DistributorDashboard /></PlayerRoute>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/operator"
                            element={
                                <ProtectedRoute>
                                    <OperatorDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/cashier"
                            element={
                                <ProtectedRoute>
                                    <CashierDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/affiliate"
                            element={
                                <ProtectedRoute>
                                    <AffiliateDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/help" element={<Help />} />
                    </Routes>
                    <DailySuperMeter />
                    <DailySuperHit />
                    <PlayerOnlyChatFab />
                    <TonkAutoDropWatcher />
                </BrowserRouter>
                <Toaster />
                <SpinBreakdownToast />
                <InstallBonusModal />
                {/* PayoutSpeedBanner disabled (Jun 2026) — the "Payments in 3 hours
                    or less" popup is off until fast payouts can be guaranteed again. */}
                {/* <PayoutSpeedBanner /> */}
                {/* Community pots fire celebration (Community Grand / BEToBuck$) removed from UI (Jun 2026) */}
                {/* <JackpotFireCelebration /> */}
                <BigWinCelebration />
                <MatchUnlockedCelebration />
                <WhatsNewModal />
                {/* Mounted LAST so its portal stacks above all other modals —
                    a forced email entry must never be hidden behind the bonus/
                    what's-new popups. */}
                <ForceRealEmailGate />
            </div>
        </AuthProvider>
    );
}

export default App;
